import {
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import React from "react";
import { styles } from "./BasketView.styles";
import { useTranslation } from "react-i18next";
import { dateFormat } from "../../utilities/dateTimeUtils";
import { ccyFormat } from "../../utilities/PriceUtils";
import * as BasketAction from "../../stores/basket/BasketAction";
import { useDispatch, useSelector } from "react-redux";
import ClearIcon from "@material-ui/icons/Clear";
import { adminFeeDefault, adminFeeLimit, deliveryMethod } from "../../constants/ORDER_DETAILS";
import { findCategoryByName, findProductByName, isAnyProductProhibitedForUser, isProductProhibitedForUser } from "./productOverview/ProductHelper";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import { KaikoDetailsComponent } from "../components/OrderDetails/KaikoDetailsComponent";
import { isEuCountry } from "../../utilities/countriesUtils";
import { selectVisibleCountries } from "../../selectors/countries/CountriesSelector";
/*
    This is the first step in the checkout, containing the table with ordered items and the table with prices.
*/

export interface IBasketViewOverviewProps extends WithStyles<typeof styles> {
    basket: {
        items: [
            id: string,
            type: string,
            product: string,
            from: number,
            to: number,
            price: number

        ],
        totalPrice: number,
        adminFee: number,
        discount: number,
        vat: number
        remark?: string,
    },
    buttons: boolean,
    userId: string,
    orderDetails: any,
    handleOrderChange: any,
    comment: boolean
}

const BasketViewOverviewPure: React.FunctionComponent<IBasketViewOverviewProps> = ({
                                                                                       classes,
                                                                                       basket,
                                                                                       buttons,
                                                                                       userId,
                                                                                       orderDetails,
                                                                                       handleOrderChange,
                                                                                       comment
                                                                                   }: IBasketViewOverviewProps) => {

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {enqueueSnackbar} = useSnackbar();
    const basketState = useSelector((state) => state.basket?.state);
    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);
    const countries = useSelector(state => selectVisibleCountries(state));

    const handleDeleteItem = (id) => {
        dispatch(BasketAction.requestBasket(userId));
        if (basketState === "Locked") {
            enqueueSnackbar("Can not delete the item from the basket during the payment process!", {variant: ToastStatusEnum.Error});
        } else {
            dispatch(BasketAction.deleteItem(userId, id, basket.vat)).then((res) => dispatch(BasketAction.requestBasket(userId)));
            enqueueSnackbar("Item deleted from the basket", {variant: ToastStatusEnum.Info});
        }
    };

    const productsInfo = useSelector((state) => state.products);

    let subtotalPrice = 0.0;
    for (let item of basket.items) {
        subtotalPrice += item["price"];
    }

    let adminFee = 0;
    if (subtotalPrice <= adminFeeLimit) {
        adminFee = adminFeeDefault;
    }
    const discount = Number(basket.discount ?? 0);

    const subtotalPriceNoVat = subtotalPrice + adminFee - discount;
    let vatValue = (subtotalPriceNoVat * Number(basket.vat)) / 100;

    if (orderDetails.paymentMethod === "Quota") {
        vatValue = 0.00;
    }

    const isAnyProductProhibited = isAnyProductProhibitedForUser(basket, productsInfo, userDetails);
    const totalPriceFrontend = subtotalPriceNoVat + vatValue;

    return basket && orderDetails && <>
        {buttons
            ? <Typography className={classes.sectionHeadline} variant="h3">{t("orderDetails.basketCheck")}</Typography>
            : null
        }
        {Object.keys(productsInfo).length === 0 ? (
            <CircularProgress />
        ) : (
            <TableContainer component={Paper} className={classes.bottomMargin}>
                <Table className={classes.table}>
                    <TableHead className={classes.cellHead}>
                        <TableRow>
                            <TableCell align="center">{t("orderDetails.position")}</TableCell>
                            <TableCell align="left">Product Category</TableCell>
                            <TableCell align="left">{t("general.product")}</TableCell>
                            <TableCell align="left">Product Remark</TableCell>
                            <TableCell align="left">{t("orderDetails.timeRange")}</TableCell>
                            <TableCell align="right">{t("general.price")}</TableCell>
                            <TableCell width="30px" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {basket?.items?.map((item: any, itemIndex) => {
                            const product = findProductByName(productsInfo?.products, item.product);
                            const isProductProhibited = isProductProhibitedForUser(product, userDetails);
                            const className = isProductProhibited ? classes.unavailableProduct : null;
                            return (
                                <TableRow key={itemIndex}>
                                    <TableCell align="center" component="th" scope="row" className={className}>{itemIndex + 1}</TableCell>
                                    <TableCell className={className}
                                        align="left">{findCategoryByName(productsInfo?.categories, product?.category)?.name}</TableCell>
                                    <TableCell className={className} align="left">{product?.name}  {item.filterableProductParameters ? <KaikoDetailsComponent dates={[dateFormat(item.from.toString()), dateFormat(item.to.toString())]} exchanges={item.filterableProductParameters.levelOne} currencies={item.filterableProductParameters.levelTwo} /> : ""}</TableCell>
                                    <TableCell className={className} align="left">{item.productRemark !== "" ? <span dangerouslySetInnerHTML={{ __html: item.productRemark }} /> : null}</TableCell>
                                    <TableCell className={className}
                                        align="left">{dateFormat(item.from.toString())}<br></br>{dateFormat(item.to.toString())}
                                    </TableCell>
                                    <TableCell className={className} align="right">{ccyFormat(item.price)}</TableCell>
                                    <TableCell align="right">
                                        {buttons
                                            ? <Tooltip title="Delete item">
                                                <IconButton onClick={() => handleDeleteItem(item["id"])}>
                                                    <ClearIcon />
                                                </IconButton>
                                            </Tooltip>
                                            : null
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
        {isAnyProductProhibited && 
            <Typography variant="caption" color="error">Highlighted products are not available in your region. Please remove them from the basket in order to finish the order.</Typography>
        }

        <Grid container spacing={3} direction="row">
            <Grid item xs={12} sm={6}>
                <Typography className={classes.topMargin} variant="h3">{t("orderDetails.orderComments")}</Typography>
                {comment
                    ? <Typography
                        variant="body2">{orderDetails.remark ? orderDetails.remark : t("orderDetails.commentLater")}</Typography>
                    : <TextField
                        className={classes.comment}
                        variant="outlined"
                        name="remark"
                        type="text"
                        id="remark"
                        multiline
                        value={orderDetails.remark}
                        rows={8}
                        onChange={handleOrderChange}
                    />
                }
            </Grid>
            <Grid item xs={12} sm={6}>
                <TableContainer className={classes.bottomMargin} component={Paper}>
                    <Table className={classes.table}>
                        <TableBody>
                            <TableRow key="subtotal">
                                <TableCell colSpan={4} align="right">{t("orderDetails.subtotal")}</TableCell>
                                <TableCell
                                    align="right">{ccyFormat(Number(subtotalPrice))}</TableCell>
                            </TableRow>
                            <TableRow key="admin_fee">
                                <TableCell colSpan={4} align="right">{t("orderDetails.adminFee")}</TableCell>
                                <TableCell align="right">{ccyFormat(adminFee)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={4} align="right">{t("orderDetails.discount")}</TableCell>
                                <TableCell align="right">{ccyFormat(discount.toFixed(2))}</TableCell>
                            </TableRow>
                            <TableRow key="delivery_method">
                                <TableCell colSpan={4} align="right">{t("orderDetails.deliveryMethod")}</TableCell>
                                <TableCell align="right">{deliveryMethod}</TableCell>
                            </TableRow>

                            {userDetails && !isEuCountry(countries, userDetails.deliveryAddress.country)
                                ? <>
                                    <TableRow>
                                        <TableCell className={classes.bold} colSpan={4} align="right">
                                            Total amount (net amount)*
                                        </TableCell>
                                        <TableCell className={classes.bold}
                                                align="right">{ccyFormat(totalPriceFrontend)}
                                        </TableCell>
                                    </TableRow>
                                </>
                                : <>
                                    <TableRow key="total_without_vat">
                                        <TableCell colSpan={4} align="right">{t("general.totalAmountNoVat")}</TableCell>
                                        <TableCell align="right">{ccyFormat(subtotalPriceNoVat)}</TableCell>
                                    </TableRow>
                                    <TableRow key="vat">
                                        <TableCell colSpan={4}
                                                align="right">{t("orderDetails.vat")} ({orderDetails.paymentMethod === "Quota" ? 0.00 : basket.vat}%)*</TableCell>
                                        <TableCell align="right">{ccyFormat(vatValue)}</TableCell>
                                    </TableRow>
                                    <TableRow key="total">
                                        <TableCell className={classes.bold} colSpan={4}
                                                align="right">{t("general.totalAmountVat")}</TableCell>
                                        <TableCell className={classes.bold} align="right">{ccyFormat(totalPriceFrontend)}</TableCell>
                                    </TableRow>
                                </>
                            }

                        </TableBody>
                    </Table>
                </TableContainer>

                {userDetails && !isEuCountry(countries, userDetails.deliveryAddress.country)
                    ? <Typography variant="body2">*please note displayed prices are net amounts without any applicable taxes</Typography>
                    : <Typography variant="body2">{t("basket.euDirective")}</Typography>
                }
                
            </Grid>
        </Grid>
    </>;
};

export const BasketViewOverview = withStyles(styles)(BasketViewOverviewPure);
